import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const WhatsAppSettings = () => {
  const { baseUrl, getHeaders, user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const [settings, setSettings] = useState({
    status: true,
    provider: 'Meta WhatsApp Cloud API',
    phoneNumber: '',
    phoneNumberId: '',
    businessAccountId: '',
    accessToken: '',
    apiVersion: 'v17.0',
    modules: {
      sales: {
        sendInvoice: false,
        paymentConfirmation: false
      },
      purchase: {
        purchaseOrder: false,
        purchaseReceived: false,
        supplierNotification: false
      },
      orders: {
        orderConfirmation: false,
        orderReady: false,
        orderCancellation: false
      },
      booking: {
        bookingConfirmation: false,
        bookingReminder: false
      },
      customer: {
        customerNotification: false
      }
    },
    templates: {
      invoiceTemplate: 'invoice_receipt',
      purchaseTemplate: 'purchase_order',
      orderTemplate: 'order_confirmation',
      paymentTemplate: 'payment_confirmation',
      bookingTemplate: 'booking_confirmation'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const active_company = localStorage.getItem('active_company') || user?.company_name;
      const active_branch = localStorage.getItem('active_branch') || user?.branch_name;

      const response = await axios.get(`${baseUrl}/api/whatsapp-settings`, {
        headers: getHeaders(active_branch, active_company)
      });

      if (response.data && Object.keys(response.data).length > 0) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Error fetching whatsapp settings:", error);
      toast.error("Failed to load WhatsApp settings");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const active_company = localStorage.getItem('active_company') || user?.company_name;
      const active_branch = localStorage.getItem('active_branch') || user?.branch_name;

      await axios.post(`${baseUrl}/api/whatsapp-settings`, settings, {
        headers: getHeaders(active_branch, active_company)
      });
      toast.success("WhatsApp Settings Saved Successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.accessToken || !settings.phoneNumberId) {
      toast.warning("Please provide Access Token and Phone Number ID to test.");
      return;
    }
    setTesting(true);
    try {
      const active_company = localStorage.getItem('active_company') || user?.company_name;
      const active_branch = localStorage.getItem('active_branch') || user?.branch_name;

      const response = await axios.post(`${baseUrl}/api/test-whatsapp-settings`, {
        accessToken: settings.accessToken,
        phoneNumberId: settings.phoneNumberId
      }, {
        headers: getHeaders(active_branch, active_company)
      });

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Test failed.");
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      toast.error("Test Connection Failed");
    } finally {
      setTesting(false);
    }
  };

  const handleModuleChange = (category, field) => {
    setSettings(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [category]: {
          ...prev.modules[category],
          [field]: !prev.modules[category][field]
        }
      }
    }));
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '24px' }}>WhatsApp Integration</h2>

        {/* API Details Section */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #E5E7EB' }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>WhatsApp Status</span>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <div style={{ position: 'relative' }}>
                <input 
                  type="checkbox" 
                  checked={settings.status} 
                  onChange={(e) => setSettings({...settings, status: e.target.checked})} 
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <div style={{ 
                  width: '44px', height: '24px', backgroundColor: settings.status ? '#10B981' : '#D1D5DB', 
                  borderRadius: '9999px', transition: 'background-color 0.2s', position: 'relative' 
                }}>
                  <div style={{
                    position: 'absolute', top: '2px', left: settings.status ? '22px' : '2px',
                    width: '20px', height: '20px', backgroundColor: 'white', borderRadius: '50%',
                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}></div>
                </div>
              </div>
              <span style={{ marginLeft: '12px', fontSize: '14px', fontWeight: '500', color: settings.status ? '#10B981' : '#6B7280' }}>
                {settings.status ? 'ON' : 'OFF'}
              </span>
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Provider</label>
              <select 
                value={settings.provider} 
                onChange={(e) => setSettings({...settings, provider: e.target.value})}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
              >
                <option value="Meta WhatsApp Cloud API">Meta WhatsApp Cloud API</option>
                <option value="Twilio">Twilio</option>
                <option value="MessageBird">MessageBird</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Phone Number</label>
                <input 
                  type="text" 
                  value={settings.phoneNumber} 
                  onChange={(e) => setSettings({...settings, phoneNumber: e.target.value})}
                  placeholder="+971XXXXXXXXX"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Phone Number ID</label>
                <input 
                  type="text" 
                  value={settings.phoneNumberId} 
                  onChange={(e) => setSettings({...settings, phoneNumberId: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>WhatsApp Business Account ID</label>
                <input 
                  type="text" 
                  value={settings.businessAccountId} 
                  onChange={(e) => setSettings({...settings, businessAccountId: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>API Version</label>
                <select 
                  value={settings.apiVersion} 
                  onChange={(e) => setSettings({...settings, apiVersion: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
                >
                  <option value="v17.0">v17.0</option>
                  <option value="v18.0">v18.0</option>
                  <option value="v19.0">v19.0</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Access Token</label>
              <input 
                type="password" 
                value={settings.accessToken} 
                onChange={(e) => setSettings({...settings, accessToken: e.target.value})}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Message / Module Settings */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
            Message / Module Settings
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
            
            {/* Sales / Invoice */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4B5563', marginBottom: '12px' }}>Sales / Invoice</h4>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.sales.sendInvoice} onChange={() => handleModuleChange('sales', 'sendInvoice')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Send Invoice</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.sales.paymentConfirmation} onChange={() => handleModuleChange('sales', 'paymentConfirmation')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Payment Confirmation</span>
              </label>
            </div>

            {/* Purchase */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4B5563', marginBottom: '12px' }}>Purchase</h4>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.purchase.purchaseOrder} onChange={() => handleModuleChange('purchase', 'purchaseOrder')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Purchase Order</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.purchase.purchaseReceived} onChange={() => handleModuleChange('purchase', 'purchaseReceived')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Purchase Received</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.purchase.supplierNotification} onChange={() => handleModuleChange('purchase', 'supplierNotification')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Supplier Notification</span>
              </label>
            </div>

            {/* Orders */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4B5563', marginBottom: '12px' }}>Orders</h4>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.orders.orderConfirmation} onChange={() => handleModuleChange('orders', 'orderConfirmation')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Order Confirmation</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.orders.orderReady} onChange={() => handleModuleChange('orders', 'orderReady')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Order Ready</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.orders.orderCancellation} onChange={() => handleModuleChange('orders', 'orderCancellation')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Order Cancellation</span>
              </label>
            </div>

            {/* Booking & Customer */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4B5563', marginBottom: '12px' }}>Booking</h4>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.booking.bookingConfirmation} onChange={() => handleModuleChange('booking', 'bookingConfirmation')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Booking Confirmation</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.booking.bookingReminder} onChange={() => handleModuleChange('booking', 'bookingReminder')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Booking Reminder</span>
              </label>
              
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4B5563', marginBottom: '12px' }}>Customer</h4>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.modules.customer.customerNotification} onChange={() => handleModuleChange('customer', 'customerNotification')} style={{ marginRight: '8px', accentColor: '#3B82F6', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '14px', color: '#374151' }}>Customer Notification</span>
              </label>
            </div>
          </div>
        </div>

        {/* Default Templates Section */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '32px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
            Default Templates
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Invoice Template</label>
              <select value={settings.templates.invoiceTemplate} onChange={(e) => setSettings({...settings, templates: {...settings.templates, invoiceTemplate: e.target.value}})} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}>
                <option value="invoice_receipt">invoice_receipt</option>
                <option value="invoice_minimal">invoice_minimal</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Purchase Template</label>
              <select value={settings.templates.purchaseTemplate} onChange={(e) => setSettings({...settings, templates: {...settings.templates, purchaseTemplate: e.target.value}})} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}>
                <option value="purchase_order">purchase_order</option>
                <option value="po_standard">po_standard</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Order Template</label>
              <select value={settings.templates.orderTemplate} onChange={(e) => setSettings({...settings, templates: {...settings.templates, orderTemplate: e.target.value}})} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}>
                <option value="order_confirmation">order_confirmation</option>
                <option value="order_alert">order_alert</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Payment Template</label>
              <select value={settings.templates.paymentTemplate} onChange={(e) => setSettings({...settings, templates: {...settings.templates, paymentTemplate: e.target.value}})} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}>
                <option value="payment_confirmation">payment_confirmation</option>
                <option value="payment_receipt">payment_receipt</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Booking Template</label>
              <select value={settings.templates.bookingTemplate} onChange={(e) => setSettings({...settings, templates: {...settings.templates, bookingTemplate: e.target.value}})} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', outline: 'none' }}>
                <option value="booking_confirmation">booking_confirmation</option>
                <option value="booking_reminder">booking_reminder</option>
              </select>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
          <button 
            onClick={handleTestConnection}
            disabled={testing}
            style={{ 
              padding: '12px 24px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', 
              borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
              opacity: testing ? 0.7 : 1
            }}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button 
            onClick={handleSave}
            disabled={loading}
            style={{ 
              padding: '12px 24px', backgroundColor: '#3B82F6', color: 'white', border: 'none', 
              borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default WhatsAppSettings;
